import React, { createContext, useContext, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppQuery } from "./hooks/useAppQuery";
import { useQueries } from "@tanstack/react-query";
import { DateTime } from "luxon";
import Loading from "./components/Loading";
import WarningAlert from "./components/WarningAlert";

import { fetchCustomerByIdpId } from "./api/usersApi";
import { fetchBatteryPowerBySiteId } from "./api/graphs_api/batteryApi";
import { fetchCombinedLoadBySiteId } from "./api/graphs_api/loadApi";
import { fetchEntsoePrices } from "./api/graphs_api/pricesApi";
import { fetchCarbonIntensity } from "./api/graphs_api/carbonIntensityApi";
import { fetchGridConsumptionBySiteId } from "./api/graphs_api/gridApi";
import { fetchCombinedSolarBySiteId } from "./api/graphs_api/solarApi";
import { fetchHealthCheckBySiteId } from "./api/deviceHealthApi";

const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth0();
  const idpId = user?.sub;
  const timeout = parseInt(window.appConfig?.getTimeout);
  const country_code = "BE";

  const { data: blob, isLoading: loadingBlob, error: errorBlob } = useAppQuery(
    ["customer_blob", idpId, timeout],
    fetchCustomerByIdpId,
    {
      enabled: Boolean(idpId && timeout),
      retry: false,
    }
  );

  const fallbackTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const cachedTimezone = localStorage.getItem("user-timezone");
  const timezone = blob?.user?.timezone || cachedTimezone || fallbackTimezone;

  const localStart = DateTime.now().setZone(timezone).startOf("day");
  const localEnd = localStart.plus({ days: 1 }).minus({ minutes: 1 });
  const start_date = localStart.toUTC().toISO();
  const end_date = localEnd.toUTC().toISO();

  const batteryQueries = useQueries({
    queries: blob
      ? blob.sites.map((site) => ({
        queryKey: ["combined-battery", site.id, start_date, end_date, timeout],
        queryFn: fetchBatteryPowerBySiteId,
        retry: false,
      }))
      : [],
  });

  const loadQueries = useQueries({
    queries: blob
      ? blob.sites.map((site) => ({
        queryKey: ["combined-load", site.id, start_date, end_date, timeout],
        queryFn: fetchCombinedLoadBySiteId,
        retry: false,
      }))
      : [],
  });

  const { data: pricesToday, isLoading: loadingPrices, error: pricesError } = useAppQuery(
    ["entsoe-prices", start_date, end_date, country_code, timeout],
    fetchEntsoePrices,
    {
      enabled: Boolean(start_date && end_date && country_code && timeout),
      retry: false,
    }
  );

  const { data: carbonToday, isLoading: loadingCarbon, error: carbonError } = useAppQuery(
    ["carbon-intensity", start_date, end_date, country_code, "15min", timeout],
    fetchCarbonIntensity,
    { enabled: Boolean(start_date && end_date && country_code && timeout), retry: false }
  );

  const gridQueries = useQueries({
    queries: blob
      ? blob.sites.map((site) => ({
        queryKey: ["grid-consumption", site.id, start_date, end_date, timeout],
        queryFn: fetchGridConsumptionBySiteId,
        retry: false,
      }))
      : [],
  });

  const solarQueries = useQueries({
    queries: blob
      ? blob.sites.map((site) => ({
        queryKey: ["combined-solar", site.id, start_date, end_date, timeout],
        queryFn: fetchCombinedSolarBySiteId,
        retry: false,
      }))
      : [],
  });

  const devicesHealthCheck = useQueries({
    queries: blob
      ? blob.sites.map((site) => ({
        queryKey: ["device-health", site.id, timeout],
        queryFn: fetchHealthCheckBySiteId,
        retry: false,
        refetchInterval: 60000,
      }))
      : [],
  });

  const loadingCombinedLoad = loadQueries.length > 0 && loadQueries.some((q) => q.isLoading);
  const loadingSchedules = batteryQueries.length > 0 && batteryQueries.some((q) => q.isLoading);
  const loadingGrids = gridQueries.length > 0 && gridQueries.some((q) => q.isLoading);
  const loadingSolars = solarQueries.length > 0 && solarQueries.some((q) => q.isLoading);
  const loadingHealthChecks = devicesHealthCheck.length > 0 && devicesHealthCheck.some((q) => q.isLoading);

  const isLoading =
    loadingBlob ||
    loadingSchedules ||
    loadingCombinedLoad ||
    loadingPrices ||
    loadingCarbon ||
    loadingGrids ||
    loadingSolars ||
    loadingHealthChecks ||
    authLoading;

  const error = errorBlob || pricesError || carbonError;

  const patchedData = useMemo(() => {
    if (!blob) return null;

    const sites = blob.sites.map((site, i) => ({
      ...site,
      schedule: batteryQueries[i]?.data ?? null,
      prices: pricesToday ?? null,
      carbon_intensity: carbonToday ?? null,
      load: loadQueries[i]?.data ?? null,
      grid: gridQueries[i]?.data ?? null,
      solar: solarQueries[i]?.data ?? null,
      health: devicesHealthCheck[i]?.data ?? null,
    }));

    return { ...blob, sites, pricesToday, carbonToday };
  }, [
    blob,
    batteryQueries,
    loadQueries,
    gridQueries,
    solarQueries,
    devicesHealthCheck,
    pricesToday,
    carbonToday
  ]);

  if (error && error.message) {
    if (error.message.includes("404 - Failed to retrieve customer data")) {
      error.message += "\n\nPlease contact customer support:\nNathan Lemaire  (tel) : +32 499431870";
    }

    const displayError = error.message.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));

    return <WarningAlert message={displayError} />;
  }

  if (isLoading || !patchedData) return <Loading />;

  return (
    <DataContext.Provider value={patchedData}>
      {children}
    </DataContext.Provider>
  );
};

export const useAppData = () => useContext(DataContext);
